import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

import { EnvironmentConfig } from "@rubbl-core";

import {
  CalculatorLeadSubmitPayload,
  HaulingProfileUpdatePayload,
  HaulingSignupPayload,
  MachinesSignupPayload,
  Lead,
  LeadDocument,
  LeadSubmitResult,
  MachineLeadCreatedResult,
  MachineLeadInput,
  ProviderKind,
  ProviderSignupResult,
  ReserveProfileUpdatePayload,
  ServiceProfileUpdatePayload,
  ServiceSignupPayload,
} from "../models/calculator-lead.model";
declare const environment: EnvironmentConfig;

/**
 * Persistence for Rubbl Connect leads (earnings calculator machines + hauling & service provider
 * profiles + documents). Talks to the Admin API lead endpoints anonymously, adding the in-memory
 * X-Connect-Token minted from the marketplace JWT for provider/document actions.
 */
@Injectable({
  providedIn: "root",
})
export class ConnectLeadService {
  private readonly baseUrl = `${environment.adminApiDomain}/api/admin/v1/lead`;
  private deviceToken: string | null = null;

  constructor(private httpClient: HttpClient) {}

  setDeviceToken(token: string | null): void {
    this.deviceToken = token || null;
  }

  getDeviceToken(): string | null {
    return this.deviceToken;
  }

  clearDeviceToken(): void {
    this.deviceToken = null;
  }

  /**
   * Create a new lead with its first machine lead, or append the machine to an existing
   * lead with the same phone number (the API dedups by phone).
   */
  submitLead(payload: CalculatorLeadSubmitPayload): Observable<LeadSubmitResult> {
    const body = {
      machine: this.toMachineRequest(payload),
      contact: payload.contact
        ? {
            phone: payload.contact.phone,
            firstName: payload.contact.firstName,
            lastName: payload.contact.lastName,
            email: payload.contact.email,
            smsOptIn: payload.contact.smsOptIn,
          }
        : null,
      reserveServiceArea: payload.reserveServiceArea ?? null,
    };

    return this.httpClient.post<LeadSubmitResult>(this.baseUrl, body);
  }

  /**
   * Fetch a lead (and its machine leads) by id.
   */
  getLead(id: string): Observable<Lead> {
    return this.httpClient.get<Lead>(`${this.baseUrl}/${id}`);
  }

  /**
   * Look up a lead by phone number (used when the device holds a verified session).
   */
  lookupLead(phone: string): Observable<Lead> {
    const params = new HttpParams().set("phone", phone);
    return this.httpClient.get<Lead>(`${this.baseUrl}/lookup`, { params });
  }

  /**
   * Request a one-time access code be SMS'd to the phone (for a new device, or to resend).
   */
  requestAccessCode(phone: string): Observable<unknown> {
    return this.httpClient.post(`${this.baseUrl}/request-access`, { phone });
  }

  /**
   * Verify the access code; returns the lead on success.
   */
  verifyAccessCode(phone: string, code: string): Observable<Lead> {
    return this.httpClient.post<Lead>(`${this.baseUrl}/verify-access`, { phone, code }).pipe(
      tap(lead => this.rememberDeviceToken(lead)),
    );
  }

  /**
   * Resume Connect from a marketplace IdentityServer JWT. Mints a per-device access token
   * when the signed-in account matches a lead (by phone). Admin is not covered by the
   * marketplace interceptor, so the Bearer header is set explicitly.
   */
  resumeSession(accessToken: string): Observable<Lead> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${accessToken}` });
    return this.httpClient.post<Lead>(`${this.baseUrl}/session`, {}, { headers }).pipe(
      tap(lead => this.rememberDeviceToken(lead)),
    );
  }

  /**
   * Update the owner's SMS opt-in preference for a capability (lets an owner opt out of texts
   * per capability — machines, hauling, or service — independently).
   */
  updateSmsPreference(
    leadId: string,
    smsOptIn: boolean,
    capability: "machines" | "hauling" | "service" = "machines",
  ): Observable<Lead> {
    return this.httpClient.put<Lead>(`${this.baseUrl}/${leadId}/sms-preference`, { smsOptIn, capability });
  }

  /**
   * Update the owner's email opt-in preference for a capability (token-guarded). Deal emails go to the
   * lead's email, so the API rejects turning email on (400) when the lead has none.
   */
  updateEmailPreference(
    leadId: string,
    emailOptIn: boolean,
    capability: "machines" | "hauling" | "service" = "machines",
  ): Observable<Lead> {
    return this.httpClient.put<Lead>(
      `${this.baseUrl}/${leadId}/email-preference`,
      { emailOptIn, capability },
      { headers: this.tokenHeaders() },
    );
  }

  /**
   * Replace the set of machine types the owner wants backup-rental alerts for (the reserve-machines
   * capability). Pass the full desired list of primary-type names; it overwrites the existing set.
   * With `allMachineTypes` the owner gets alerts for every type and the API clears the list.
   */
  updateMachineTypeSubscriptions(leadId: string, machineTypes: string[], allMachineTypes = false): Observable<Lead> {
    return this.httpClient.put<Lead>(`${this.baseUrl}/${leadId}/machine-type-subscriptions`, { machineTypes, allMachineTypes });
  }

  /**
   * Update the owner's name and email. The phone number is changed separately via the
   * phone-change OTP flow, so it is not part of this request.
   */
  updateContact(leadId: string, contact: { firstName?: string; lastName?: string; email?: string }): Observable<Lead> {
    return this.httpClient.put<Lead>(`${this.baseUrl}/${leadId}/contact`, contact);
  }

  /**
   * Begin a phone-number change: text a one-time code to the new number so the owner can
   * confirm they control it.
   */
  requestPhoneChange(leadId: string, phone: string): Observable<unknown> {
    return this.httpClient.post(`${this.baseUrl}/${leadId}/request-phone-change`, { phone });
  }

  /**
   * Verify the phone-change code; returns the updated lead (with the new phone) on success.
   */
  verifyPhoneChange(leadId: string, code: string): Observable<Lead> {
    return this.httpClient.post<Lead>(`${this.baseUrl}/${leadId}/verify-phone-change`, { code });
  }

  /**
   * Add another machine lead to an existing lead.
   */
  addMachineLead(leadId: string, machine: MachineLeadInput): Observable<MachineLeadCreatedResult> {
    return this.httpClient.post<MachineLeadCreatedResult>(`${this.baseUrl}/${leadId}/machine-lead`, this.toMachineRequest(machine));
  }

  /**
   * Update an existing machine lead.
   */
  updateMachineLead(leadId: string, machineLeadId: string, machine: MachineLeadInput): Observable<unknown> {
    return this.httpClient.put(`${this.baseUrl}/${leadId}/machine-lead/${machineLeadId}`, this.toMachineRequest(machine));
  }

  /**
   * Delete a machine lead from a lead.
   */
  deleteMachineLead(leadId: string, machineLeadId: string): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/${leadId}/machine-lead/${machineLeadId}`);
  }

  // ───────────────────────────────────────────────────────────────────────
  // Hauling / Service providers
  // ───────────────────────────────────────────────────────────────────────

  /** Public hauling-provider signup (dedups by phone server-side). */
  submitHauling(payload: HaulingSignupPayload): Observable<ProviderSignupResult> {
    return this.httpClient.post<ProviderSignupResult>(`${this.baseUrl}/hauling-signup`, payload);
  }

  /** Public service-provider signup (dedups by phone server-side). */
  submitService(payload: ServiceSignupPayload): Observable<ProviderSignupResult> {
    return this.httpClient.post<ProviderSignupResult>(`${this.baseUrl}/service-signup`, payload);
  }

  /** Public machines / backup-rental signup (dedups by phone server-side). */
  submitMachines(payload: MachinesSignupPayload): Observable<ProviderSignupResult> {
    return this.httpClient.post<ProviderSignupResult>(`${this.baseUrl}/machines-signup`, payload);
  }

  /** Update a hauling profile (token-guarded — /connect tab). */
  updateHaulingProfile(leadId: string, payload: HaulingProfileUpdatePayload): Observable<Lead> {
    return this.httpClient.put<Lead>(`${this.baseUrl}/${leadId}/hauling`, payload, {
      headers: this.tokenHeaders(),
    });
  }

  /** Update a service profile (token-guarded — /connect tab). */
  updateServiceProfile(leadId: string, payload: ServiceProfileUpdatePayload): Observable<Lead> {
    return this.httpClient.put<Lead>(`${this.baseUrl}/${leadId}/service`, payload, {
      headers: this.tokenHeaders(),
    });
  }

  /** Update the reserve profile's service area (token-guarded — /connect Reserve Machines tab). */
  updateReserveProfile(leadId: string, payload: ReserveProfileUpdatePayload): Observable<Lead> {
    return this.httpClient.put<Lead>(`${this.baseUrl}/${leadId}/reserve`, payload, {
      headers: this.tokenHeaders(),
    });
  }

  /** Upload an optional provider document (token-guarded). */
  uploadProviderDocument(
    leadId: string,
    profile: ProviderKind,
    type: string,
    file: File,
  ): Observable<LeadDocument> {
    const formData = new FormData();
    formData.append("type", type);
    formData.append("file", file, file.name);
    return this.httpClient.post<LeadDocument>(
      `${this.baseUrl}/${leadId}/${profile}/document`,
      formData,
      { headers: this.tokenHeaders() },
    );
  }

  /** Build the authenticated download URL for a provider document (token passed as a query param). */
  documentDownloadUrl(leadId: string, profile: ProviderKind, documentId: string): string {
    const token = encodeURIComponent(this.deviceToken || "");
    return `${this.baseUrl}/${leadId}/${profile}/document/${documentId}?token=${token}`;
  }

  /** Delete a provider document (token-guarded). */
  deleteProviderDocument(leadId: string, profile: ProviderKind, documentId: string): Observable<unknown> {
    return this.httpClient.delete(`${this.baseUrl}/${leadId}/${profile}/document/${documentId}`, {
      headers: this.tokenHeaders(),
    });
  }

  private rememberDeviceToken(lead: Lead | null): void {
    if (lead?.accessToken) {
      this.deviceToken = lead.accessToken;
    }
  }

  private tokenHeaders(): HttpHeaders {
    return new HttpHeaders().set("X-Connect-Token", this.deviceToken || "");
  }

  private toMachineRequest(machine: MachineLeadInput | CalculatorLeadSubmitPayload) {
    return {
      machineType: machine.machineType,
      machineTypeDefinitionId: machine.machineTypeDefinitionId,
      make: machine.make,
      model: machine.model,
      year: machine.year,
      hours: machine.hours,
      isMileageBased: machine.isMileageBased,
      location: machine.location,
      rates: {
        monthlyRate: machine.rates.monthlyRate,
        weeklyRate: machine.rates.weeklyRate,
      },
    };
  }
}
